// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { color } from "@hardwoods/XDL/Utilities";
import usePrevious from "@insite/client-framework/Common/Hooks/usePrevious";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import isEqual from "lodash/isEqual";
import React, { useCallback, useEffect, useState } from "react";
import { Transition } from "react-transition-group";
import { TransitionStatus } from "react-transition-group/Transition";
import styled, { css, ThemeProps } from "styled-components";

const ScrollIndicator = styled.span<
    { direction: "up" | "down"; transitionState: TransitionStatus } & ThemeProps<BaseTheme>
>`
    position: absolute;
    width: 100%;
    height: 80px;
    max-height: 30%;
    z-index: 1;
    color: ${color("common.white")};
    display: flex;
    justify-content: center;
    transition: all ease-in-out 250ms;
    padding-bottom: 12px;
    ${({ transitionState }) => {
        switch (transitionState) {
            case "entering":
                return "opacity: 0;";
            case "entered":
                return "opacity: 1;";
            case "exiting":
            case "exited":
                return "opacity: 0;";
            default:
                return "";
        }
    }}
    ${({ direction }) => {
        if (direction === "up") {
            return css`
                align-items: flex-start;
                background: linear-gradient(0deg, rgba(255, 255, 255, 0) 0%, ${color("common.white")} 100%);
            `;
        }
        if (direction === "down") {
            return css`
                align-items: flex-end;
                background: linear-gradient(180deg, rgba(255, 255, 255, 0) 0%, ${color("common.white")} 100%);
                bottom: 0;
            `;
        }
    }}
`;

const FancyScroll = ({
    children,
    refElement,
    showUp,
    updatedConditions,
}: {
    children: React.ReactElement;
    refElement: React.RefObject<HTMLElement>;
    showUp: boolean;
    updatedConditions?: any[];
}) => {
    const [showScrollDown, setShowScrollDown] = useState(false);
    const [showScrollUp, setShowScrollUp] = useState(false);
    const updatedConditionsPrevious = usePrevious(updatedConditions);

    useEffect(() => {
        setTimeout(() => checkScollable());
    }, []);

    useEffect(() => {
        if (!isEqual(updatedConditions, updatedConditionsPrevious)) {
            setTimeout(() => checkScollable());
        }
    }, [updatedConditions]);

    const checkScollable = useCallback(() => {
        if (refElement.current) {
            if (
                refElement.current.scrollHeight > Math.ceil(refElement.current.offsetHeight) &&
                refElement.current.scrollTop > 0
            ) {
                setShowScrollUp(true);
            } else {
                setShowScrollUp(false);
            }

            if (
                refElement.current.scrollHeight >
                Math.ceil(refElement.current.offsetHeight + refElement.current.scrollTop)
            ) {
                setShowScrollDown(true);
            } else {
                setShowScrollDown(false);
            }
        }
    }, [refElement]);

    useEffect(() => {
        if (refElement && refElement.current) {
            setTimeout(() => checkScollable());
            refElement.current.addEventListener("scroll", checkScollable);
        }
        return () => {
            if (refElement && refElement.current) {
                refElement.current.removeEventListener("scroll", checkScollable);
            }
        };
    }, [refElement?.current?.offsetHeight]);

    return (
        <StyledWrapper
            css={css`
                position: relative;
            `}
        >
            {showUp && (
                <Transition mountOnEnter unmountOnExit in={showScrollUp} timeout={{ enter: 75, exit: 250 }}>
                    {state => <ScrollIndicator direction="up" transitionState={state}></ScrollIndicator>}
                </Transition>
            )}
            {children}
            <Transition mountOnEnter unmountOnExit in={showScrollDown} timeout={{ enter: 75, exit: 250 }}>
                {state => <ScrollIndicator direction="down" transitionState={state}></ScrollIndicator>}
            </Transition>
        </StyledWrapper>
    );
};

export default FancyScroll;
